




















































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { IAiPhraseVariable, IAiPhraseRecord, IAiPhraseVariableInvalid, IAiPhraseRecordInformation } from "../../../../../domain/externalSystem";
import { BrowserKinds, BrowserUtility } from "../../../../../utilities";
import { Separator } from "../../../../system";
import { ModelEditableMixin } from "../../../../system/mixins";
import AiPhraseRecordViewer from "./AiPhraseRecordViewer.vue";
import { ClickableTextContentFactory, ClickableTextContent } from "./model";

type ContentType = "fixed" | "variable" | "cr";
interface ContentBase<T extends ContentType> {
  type: T;
}
interface FixedContent extends ContentBase<"fixed"> {
  value: string;
}
interface VariableContent extends ContentBase<"variable"> {
  key: string;
}
interface CrContent extends ContentBase<"cr"> {
}
type Content = FixedContent | VariableContent | CrContent;

class ConentsFactory {
  static createContents(template: string, variables: { [key: string]: IAiPhraseVariable }): Content[] {
    // cocktailbarから移植した実装でcontentsを生成してからDocuMaker3の型に変換.
    const contents = ClickableTextContentFactory.create({
      template: template,
      variables: variables
        ? Object.keys(variables).map(k => ({
          source: {
            key: k,
            isValid: variables[k].isValid,
            value: variables[k].value,
          },
          value: variables[k].value,
        }))
        : [],
    });
    return this.flat(contents.map(c => this.toContents(c)));
  }

  private static toContents(source: ClickableTextContent<string>): Content[] {
    if (source.type === "container") {
      // containerはDocuMaker3実装にはない概念なのでバラす.
      return source.contents.length > 0
        ? source.contents[0].type === "clickable"
          ? this.toContents(source.contents[0])   // 結果に改行入る場合はcocktailbar実装では分割されてる.DocuMaker3では不要なので先頭の1つだけを扱う.
          : this.flat(source.contents.map(c => this.toContents(c)))
        : [];
    } else if (source.type === "clickable") {
      return [{
        type: "variable",
        key: source.content,
      }];
    } else if (source.type === "fixed") {
      return [{
        type: "fixed",
        value: source.text,
      }];
    } else if (source.type === "cr") {
      return [{
        type: "cr",
      }];
    }
  }

  private static flat<T>(source: T[][]): T[] {
    return ([] as T[]).concat(...source);
  }
}

@Component({
  components: {
    AiPhraseRecordViewer,
    Separator,
  }
})
export default class AiPhraseEditor extends Mixins(ModelEditableMixin) {
  @Prop()
  template: string;

  @Prop()
  variables: { [key: string]: IAiPhraseVariable };

  contents: Content[] = null;
  selectedPhraseKey: string | null = null;
  editedPhrases: { [key: string]: string } = {};

  created(): void {
    this.selectorWidth = this.selectorDefaultWidth;
    this.createdPhraseEditorHeight = this.contentDefaultHeight;
    this.createdPhraseHeight = this.contentDefaultHeight;
    this.evidencesViewerHeight = this.contentDefaultHeight;
  }

  data(): any {
    return {
      mergedPhrases: null,
      selectorWidth: null,
      createdPhraseEditorHeight: null,
      createdPhraseHeight: null,
      evidencesViewerHeight: null,
    };
  }
  mergedPhrases: { [key: string]: IAiPhraseVariable };
  selectorWidth: number;
  createdPhraseEditorHeight: number;
  createdPhraseHeight: number;
  evidencesViewerHeight: number;

  get browserStyle(): any {
    return (BrowserUtility.getBrowserKind() === BrowserKinds.InternetExplorer)
      ? {
        zIndex: 0, // IEでseparatorを動かして要素を重ねると下の要素が見えるため
      }
      : null;
  }

  get selectorDefaultWidth(): number {
    return (((window.innerWidth) * 0.95) - 50) / 2;
  }

  get contentDefaultHeight(): number {
    return ((window.innerHeight * 0.87) - 305) / 3;
  }

  get isPhraseCreated(): boolean {
    return !!this.variables;
  }

  get selectedPhrase(): IAiPhraseVariable | undefined {
    return this.mergedPhrases
      ? this.mergedPhrases[this.selectedPhraseKey]
      : undefined;
  }

  get isPhraseSelected(): boolean {
    return !!this.selectedPhrase;
  }

  get isSelectePhraseValid(): boolean {
    return !!this.selectedPhrase && this.selectedPhrase.isValid;
  }

  get selectedPhraseSource(): string {
    if (!this.selectedPhrase) {
      return "";
    }
    return this.selectedPhrase.isValid
      ? this.selectedPhrase.value
      : (this.selectedPhrase as IAiPhraseVariableInvalid).reason; // なぜか型推論効かない.(TypeScriptが古いから？)castする.
  }

  get selectedPhraseSourceClass() {
    return this.isSelectePhraseValid ? undefined : "invalid";
  }

  get hasEvidences(): boolean {
    return 0 < this.evidences.length;
  }

  get evidences(): IAiPhraseRecord[] {
    return this.selectedPhrase && this.selectedPhrase.isValid
      ? this.selectedPhrase.evidences
      : [];
  }

  get recordInformation(): IAiPhraseRecordInformation[] {
    return this.evidences
      .map(e => ({ source: e }));
  }

  get isSelectedPhraseEdited(): boolean {
    return this.selectedPhrase && this.selectedPhrase.value !== this.editedPhrases[this.selectedPhraseKey];
  }

  get selectedPhraseEdittingValue(): string {
    return this.editedPhrases[this.selectedPhraseKey];
  }

  set selectedPhraseEdittingValue(value: string) {
    const cloned = { ...this.editedPhrases };
    cloned[this.selectedPhraseKey] = value;
    this.editedPhrases = cloned;
  }

  get selectedPhraseEditttedClass() {
    return this.isSelectedPhraseEdited ? "edited" : undefined;
  }

  revertSelectedPhraseEdittingValue(): void {
    this.selectedPhraseEdittingValue = this.selectedPhrase.value;
  }

  toVariableClass(source: VariableContent): string[] {
    const isCreated = this.isPhraseCreated;
    const isInvalid = !this.mergedPhrases || !this.mergedPhrases[source.key] || !this.mergedPhrases[source.key].isValid;
    const isSelected = this.selectedPhraseKey === source.key;
    const isEdited = this.mergedPhrases && this.mergedPhrases[source.key] && this.mergedPhrases[source.key].value !== this.editedPhrases[source.key];
    return [
      "variable",
      isCreated ? "created" : undefined,
      isInvalid ? "invalid" : undefined,
      isSelected ? "selected" : undefined,
      isEdited ? "edited" : undefined,
    ].filter(c => !!c);
  }

  toVariableTitle(source: VariableContent): string {
    return "{" + source.key + "}\nクリックで選択すると根拠を確認したり、値を変更したりできます。";
  }

  toVariableContent(source: VariableContent): string {
    return this.editedPhrases[source.key]
      ? this.editedPhrases[source.key]
      : "{" + source.key + "}";
  }

  onVariableClicked(source: VariableContent): void {
    this.selectedPhraseKey = this.isPhraseCreated && this.selectedPhraseKey !== source.key ? source.key : undefined;
  }

  mounted() {
    this.initializeModel();
  }

  @Watch("template")
  onTemplateChanged() {
    this.initializeModel();
  }

  @Watch("variables")
  onVariablesChanged() {
    this.initializeModel();
  }

  private createMergedPhrases(): { [key: string]: IAiPhraseVariable } {
    if (!this.template || !this.variables) { return this.variables; }
    return Object.keys(this.variables).reduce<{ [key: string]: IAiPhraseVariable }>((r, k) => {
      const v = this.variables[k];
      if (this.template.includes("{*" + k + "}")) {
        r["*" + k] = v;
      }
      r[k] = v.isValid
        ? {
          isValid: true,
          value : v.value.replace(/\r?\n/g, ""),
          evidences: v.evidences,
        }
        : v;
      return r;
    }, {});
  }

  private initializeModel(): void {
    this.selectedPhraseKey = null;
    if (!this.template) {
      return;
    }
    this.mergedPhrases = this.createMergedPhrases();
    this.contents = ConentsFactory.createContents(this.template, this.mergedPhrases);
    this.editedPhrases = this.mergedPhrases
      ? Object.keys(this.mergedPhrases).reduce<{ [key: string]: string }>((r, k) => {
          r[k] = this.mergedPhrases[k].value;
          return r;
        }, {})
      : {};
  }

  @Watch("editedPhrases")
  onEditedPhrasesChanged(): void {
    this.editingSource = this.editedPhrases
      ? Object.keys(this.editedPhrases).reduce<string>((r, k) => r.replace(new RegExp("\\{\\" + k + "\\}", "g"), this.editedPhrases[k]), this.template)
      : this.template;
  }

  getGeometryNames(isWindowRectangleHandleable: boolean): string[] {
    return [
      "ペインのサイズ",
    ];
  }
}
